import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, User, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  author?: {
    id: number;
    username: string;
    email: string;
  };
  createdAt: string;
  category?: string;
  featuredImage?: {
    url?: string;
    alt?: string;
  } | string;
  slug: string;
  status: string;
}

const BlogPage: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await api.get('/blog/posts');
      const posts = response.data.data?.posts || [];
      setBlogPosts(posts);
    } catch (err: any) {
      console.error('Error fetching blog posts:', err);
      // Don't show error - just hide the page gracefully
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // If no posts available (API down or empty), redirect home
  if (!loading && blogPosts.length === 0) {
    return (
      <>
        <Helmet>
          <title>Blog - iModernize</title>
        </Helmet>
        <div className="py-20">
          <div className="container-custom text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Coming Soon</h1>
            <p className="text-xl text-gray-600 mb-8">We're working on great content. Check back soon!</p>
            <Link to="/" className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </>
    );
  }

  const calculateReadTime = (excerpt: string) => {
    const wordsPerMinute = 200;
    const words = excerpt.split(' ').length;
    return Math.ceil(words / wordsPerMinute) || 5;
  };

  return (
    <>
      <Helmet>
        <title>Blog - iModernize</title>
        <meta name="description" content="Read our latest articles about web design, development, and digital marketing." />
      </Helmet>

      <div className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with the latest trends, tips, and insights in web design and development.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Loading blog posts...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link 
                  to={`/blog/${post.slug}`} 
                  key={post.id} 
                  className="block"
                >
                  <article className="card overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full">
                    {(() => {
                      const imageUrl = typeof post.featuredImage === 'string' 
                        ? post.featuredImage 
                        : post.featuredImage?.url;
                      
                      return imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={typeof post.featuredImage === 'object' && post.featuredImage?.alt ? post.featuredImage.alt : post.title}
                          className="w-full h-48 object-cover"
                        />
                      ) : (
                        <div className="bg-gradient-to-br from-primary-400 to-primary-600 h-48 flex items-center justify-center">
                          <span className="text-white text-lg font-semibold">{post.category || 'Blog'}</span>
                        </div>
                      );
                    })()}
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        {post.category && (
                          <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded text-xs font-medium mr-3">
                            {post.category}
                          </span>
                        )}
                        <Calendar className="h-4 w-4 mr-1" />
                        <span className="mr-3">{new Date(post.createdAt).toLocaleDateString()}</span>
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{calculateReadTime(post.excerpt)} min read</span>
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        {post.author && (
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">{post.author.username}</span>
                          </div>
                        )}
                        <span className="text-primary-600 hover:text-primary-700 font-medium text-sm ml-auto">
                          Read More →
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;